/**
 * Row-view (list) components — one TableRow per order per stage.
 * Callers wrap these in <Table><TableHeader>…<TableBody>…</Table>.
 */
import {
  Navigation, Bell, RotateCcw,
  Timer, Check, MoreHorizontal, Pause, Play, Phone, Loader2, ExternalLink,
} from 'lucide-react';
import { Trans } from '@lingui/react/macro';
import { useLingui } from '@lingui/react/macro';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { TableRow, TableHead, TableCell } from '@/components/ui/table';
import { formatElapsed, useTicketElapsed } from '@/lib/elapsed';
import type { BoardOrder } from '@/lib/api/services/orders.api';
import { StatePill, PrioPill, ChefAvatar, ServiceChip, deriveBoardState } from './OrderPrimitives';

// ─── Cooking row ──────────────────────────────────────────────────────────────

export function CookingRowHeader() {
  const { t } = useLingui();
  return (
    <TableRow>
      <TableHead>{t`Ticket`}</TableHead>
      <TableHead>{t`Order`}</TableHead>
      <TableHead>{t`Chef`}</TableHead>
      <TableHead>{t`Time`}</TableHead>
      <TableHead>{t`Status`}</TableHead>
      <TableHead className="text-right">{t`Actions`}</TableHead>
    </TableRow>
  );
}

export function CookingRow({ order, onDismiss, completePending }: {
  order: BoardOrder;
  onDismiss: (id: number) => void;
  completePending?: boolean;
}) {
  const { t } = useLingui();
  const state = deriveBoardState(order, 'cooking');
  const elapsedSeconds = useTicketElapsed(order);
  const tableLabel = order.table
    ? `${order.table.section} ${order.table.name}`
    : order.guestName ?? '—';

  return (
    <TableRow>
      <TableCell className="font-black tabular-nums">{order.tkt}</TableCell>
      <TableCell>
        <div className="font-semibold text-(--color-text-primary) truncate">{order.title}</div>
        <div className="text-[0.75rem] text-(--color-text-secondary) truncate">
          {tableLabel}
          {order.guestCount != null && ` · ${order.guestCount}g`}
          {order.allergens.length > 0 && ` · ${order.allergens.join(', ')}`}
        </div>
      </TableCell>
      <TableCell>
        {order.chefName ? (
          <div className="flex items-center gap-1.5 text-[0.8125rem]">
            <ChefAvatar name={order.chefName} className="w-5 h-5 shrink-0" />
            {order.chefName}
          </div>
        ) : (
          <span className="text-[0.75rem] text-(--color-text-secondary) italic"><Trans>Unassigned</Trans></span>
        )}
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-1.5">
          <Timer className="h-4 w-4 text-(--color-text-secondary) shrink-0" />
          <span className="text-sm font-semibold">{formatElapsed(elapsedSeconds)} / {order.targetMinutes}m</span>
        </div>
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-1.5">
          <StatePill state={state} />
          <PrioPill prio={order.priority} />
        </div>
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-1.5 justify-end">
          <Button variant="outline" size="icon" title={order.isPaused ? t`Resume` : t`Pause`}
            className="h-8 w-8 rounded-md">
            {order.isPaused ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
          </Button>
          <Button variant="outline" size="icon" title={t`More`} className="h-8 w-8 rounded-md">
            <MoreHorizontal className="h-4 w-4" />
          </Button>
          <Button
            size="icon"
            onClick={() => onDismiss(order.id)}
            title={t`Complete`}
            disabled={completePending}
            className="h-8 w-8 rounded-md bg-(--color-success) hover:bg-(--color-success-hover) text-(--brand-white) disabled:opacity-70"
          >
            {completePending
              ? <Loader2 className="h-4 w-4 animate-spin" />
              : <Check className="h-4 w-4" />}
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}

// ─── Plating row ──────────────────────────────────────────────────────────────

export function PlatingRowHeader() {
  const { t } = useLingui();
  return (
    <TableRow>
      <TableHead>{t`Ticket`}</TableHead>
      <TableHead>{t`Order`}</TableHead>
      <TableHead>{t`Chef`}</TableHead>
      <TableHead>{t`Status`}</TableHead>
      <TableHead className="text-right">{t`Actions`}</TableHead>
    </TableRow>
  );
}

export function PlatingRow({ order, onPass, passPending }: {
  order: BoardOrder;
  onPass: (id: number) => void;
  passPending?: boolean;
}) {
  const { t } = useLingui();
  const tableLabel = order.table
    ? `${order.table.section} ${order.table.name}`
    : order.guestName ?? '—';

  return (
    <TableRow className="border-(--color-order-plating-bg)">
      <TableCell className="font-black tabular-nums">{order.tkt}</TableCell>
      <TableCell>
        <div className="font-semibold text-(--color-text-primary) truncate">{order.title}</div>
        <div className="text-[0.75rem] text-(--color-text-secondary) truncate">
          {tableLabel}
          {order.allergens.length > 0 && ` · ${order.allergens.join(', ')}`}
        </div>
      </TableCell>
      <TableCell>
        {order.chefName ? (
          <div className="flex items-center gap-1.5 text-[0.8125rem]">
            <ChefAvatar name={order.chefName} className="w-5 h-5 shrink-0" />
            {order.chefName}
          </div>
        ) : (
          <span className="text-[0.75rem] text-(--color-text-secondary) italic"><Trans>Unassigned</Trans></span>
        )}
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-1.5">
          <StatePill state="plating" />
          <PrioPill prio={order.priority} />
        </div>
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-1.5 justify-end">
          <Button variant="outline" size="icon" title={t`More`} className="h-8 w-8 rounded-md">
            <MoreHorizontal className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="icon" title={t`Send back to cooking`} className="h-8 w-8 rounded-md">
            <RotateCcw className="h-4 w-4" />
          </Button>
          <Button
            size="icon"
            onClick={() => onPass(order.id)}
            title={t`Mark Ready`}
            disabled={passPending}
            className="h-8 w-8 rounded-md bg-(--color-order-plating-fg) hover:bg-(--color-order-plating-fg)/90 text-(--brand-white) disabled:opacity-70"
          >
            {passPending
              ? <Loader2 className="h-4 w-4 animate-spin" />
              : <Bell className="h-4 w-4" />}
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}

// ─── Pickup row ───────────────────────────────────────────────────────────────

export function PickupRowHeader() {
  const { t } = useLingui();
  return (
    <TableRow>
      <TableHead>{t`Ticket`}</TableHead>
      <TableHead>{t`Order`}</TableHead>
      <TableHead>{t`Customer`}</TableHead>
      <TableHead>{t`Phone`}</TableHead>
      <TableHead>{t`Status`}</TableHead>
      <TableHead className="text-right">{t`Actions`}</TableHead>
    </TableRow>
  );
}

export function PickupRow({ order, onDismiss }: {
  order: BoardOrder;
  onDismiss: (id: number) => void;
}) {
  const { t } = useLingui();
  const state = deriveBoardState(order, 'pickup');
  const tableLabel = order.table ? `${order.table.section} ${order.table.name}` : '—';

  return (
    <TableRow>
      <TableCell className="font-black tabular-nums">{order.tkt}</TableCell>
      <TableCell>
        <div className="font-semibold text-(--color-text-primary) truncate">{order.title}</div>
        <div className="text-[0.75rem] text-(--color-text-secondary) truncate">{tableLabel}</div>
      </TableCell>
      <TableCell>
        <span className="text-sm font-semibold">{order.guestName ?? '—'}</span>
      </TableCell>
      <TableCell>
        <span className="font-mono text-[0.8125rem]">{order.phone ?? '—'}</span>
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-1.5">
          <StatePill state={state} />
          <PrioPill prio={order.priority} />
        </div>
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-1.5 justify-end">
          {order.phone && (
            <Button variant="outline" size="icon" title={t`Call`} className="h-8 w-8 rounded-md">
              <Phone className="h-4 w-4" />
            </Button>
          )}
          <Button
            size="icon"
            onClick={() => onDismiss(order.id)}
            title={t`Hand off`}
            className="h-8 w-8 rounded-md bg-(--brand-blue) hover:bg-(--brand-blue)/90 text-(--brand-white)"
          >
            <Check className="h-4 w-4" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}

// ─── Delivery row ─────────────────────────────────────────────────────────────

export function DeliveryRowHeader() {
  const { t } = useLingui();
  return (
    <TableRow>
      <TableHead>{t`Ticket`}</TableHead>
      <TableHead>{t`Order`}</TableHead>
      <TableHead>{t`Customer`}</TableHead>
      <TableHead>{t`Courier`}</TableHead>
      <TableHead>{t`Status`}</TableHead>
      <TableHead className="text-right">{t`Actions`}</TableHead>
    </TableRow>
  );
}

export function DeliveryRow({ order, onDismiss }: {
  order: BoardOrder;
  onDismiss: (id: number) => void;
}) {
  const { t } = useLingui();
  const state = deriveBoardState(order, 'delivery');

  return (
    <TableRow>
      <TableCell className="font-black tabular-nums">{order.tkt}</TableCell>
      <TableCell>
        <div className="font-semibold text-(--color-text-primary) truncate">{order.title}</div>
        <div className="text-[0.75rem] text-(--color-text-secondary)">{order.tkt}</div>
      </TableCell>
      <TableCell>
        <span className="text-sm font-semibold">{order.guestName ?? '—'}</span>
      </TableCell>
      <TableCell>
        {order.chefName ? (
          <div className="flex items-center gap-1.5 text-[0.8125rem]">
            <ChefAvatar name={order.chefName} className="w-5 h-5 shrink-0" />
            {order.chefName}
          </div>
        ) : (
          <span className="text-[0.75rem] text-(--color-text-secondary) italic"><Trans>Unassigned</Trans></span>
        )}
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-1.5">
          <StatePill state={state} />
          <PrioPill prio={order.priority} />
        </div>
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-1.5 justify-end">
          <Button variant="outline" size="icon" title={t`Track`} className="h-8 w-8 rounded-md">
            <Navigation className="h-4 w-4" />
          </Button>
          {order.phone && (
            <Button variant="outline" size="icon" title={t`Call`} className="h-8 w-8 rounded-md">
              <Phone className="h-4 w-4" />
            </Button>
          )}
          <Button
            size="icon"
            onClick={() => onDismiss(order.id)}
            title={t`Delivered`}
            className="h-8 w-8 rounded-md bg-(--color-text-primary) hover:bg-(--color-text-primary)/90 text-(--color-surface-page)"
          >
            <Check className="h-4 w-4" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}

// ─── Completed row ────────────────────────────────────────────────────────────

export function CompletedRowHeader() {
  const { t } = useLingui();
  return (
    <TableRow>
      <TableHead>{t`Ticket`}</TableHead>
      <TableHead>{t`Order`}</TableHead>
      <TableHead>{t`Service`}</TableHead>
      <TableHead>{t`Chef`}</TableHead>
      <TableHead className="text-right">{t`Sent`}</TableHead>
    </TableRow>
  );
}

export function CompletedRow({ order }: { order: BoardOrder }) {
  const { t } = useLingui();
  return (
    <TableRow>
      <TableCell className="font-black tabular-nums">{order.tkt}</TableCell>
      <TableCell>
        <div className="font-semibold text-(--color-text-primary) truncate">{order.title}</div>
        {order.table && (
          <div className="text-[0.75rem] text-(--color-text-secondary) mt-0.5">
            {order.table.section} {order.table.name}
          </div>
        )}
      </TableCell>
      <TableCell><ServiceChip service={order.serviceType} /></TableCell>
      <TableCell>
        {order.chefName ? (
          <div className="flex items-center gap-1.5 text-[0.8125rem]">
            <ChefAvatar name={order.chefName} className="w-5 h-5" />
            {order.chefName}
          </div>
        ) : (
          <span className="text-(--color-text-secondary) italic text-[0.75rem]"><Trans>Unassigned</Trans></span>
        )}
      </TableCell>
      <TableCell>
        <div className="flex items-center justify-end gap-1.5">
          {order.sentAt && (
            <Badge variant="success">
              {new Date(order.sentAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </Badge>
          )}
          <Button variant="ghost" size="icon" className="h-7 w-7 rounded-md" title={t`Open`}>
            <ExternalLink className="h-3.5 w-3.5" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}
